import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { format } from "date-fns";
import {
  UpdatePatientSchema,
  type Patient,
  type UpdatePatientInput,
} from "@clinic/types";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardTitle } from "@/components/ui/card";
import { FieldError, Input, Label, Textarea } from "@/components/ui/input";

export function PatientInfoTab({ patient }: { patient: Patient }) {
  const queryClient = useQueryClient();
  const form = useForm<UpdatePatientInput>({
    resolver: zodResolver(UpdatePatientSchema),
    defaultValues: {
      fullName: patient.fullName,
      dob: format(new Date(patient.dob), "yyyy-MM-dd"),
      idNumber: patient.idNumber,
      phone: patient.phone,
      email: patient.email ?? "",
      address: patient.address ?? "",
      notes: patient.notes ?? "",
    },
  });

  const update = useMutation({
    mutationFn: (input: UpdatePatientInput) => api.patients.update(patient.id, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["patient", patient.id] });
      queryClient.invalidateQueries({ queryKey: ["patients"] });
    },
  });

  return (
    <Card className="max-w-2xl">
      <CardTitle>Patient details</CardTitle>
      <form onSubmit={form.handleSubmit((v) => update.mutate(v))} className="space-y-4">
        <div>
          <Label>Full name</Label>
          <Input {...form.register("fullName")} />
          <FieldError message={form.formState.errors.fullName?.message} />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Date of birth</Label>
            <Input type="date" {...form.register("dob")} />
          </div>
          <div>
            <Label>ID number</Label>
            <Input {...form.register("idNumber")} />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Phone</Label>
            <Input {...form.register("phone")} />
          </div>
          <div>
            <Label>Email</Label>
            <Input type="email" {...form.register("email")} />
            <FieldError message={form.formState.errors.email?.message} />
          </div>
        </div>
        <div>
          <Label>Address</Label>
          <Input {...form.register("address")} />
        </div>
        <div>
          <Label>Notes</Label>
          <Textarea {...form.register("notes")} rows={4} />
        </div>
        {update.isError && (
          <p className="text-sm text-danger">
            {update.error instanceof Error ? update.error.message : "Update failed"}
          </p>
        )}
        {update.isSuccess && <p className="text-sm text-success">Saved.</p>}
        <Button type="submit" loading={update.isPending}>
          Save changes
        </Button>
      </form>
    </Card>
  );
}
